import * as Sentry from '@sentry/react'

import { useState, useEffect } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import mixpanel from 'mixpanel-browser'

import { AuthContext } from 'contexts'

import { UserCompanyType } from 'types/userCompany.types'
import { CompanyMaturityType } from 'types/maturityQuestionnaire.types'
import { CompanyMinimalType } from 'types/company.types'

import service from 'service'
import { getMixPanelAuthKey } from 'service/env'
import routes from 'constants/routes'
import { LoadingTrails } from 'components'

const AuthProvider = () => {
  const navigate = useNavigate()

  const [user, setUser] = useState({})
  const [userCompany, setUserCompany] = useState<UserCompanyType>({})
  const [companyMaturity, setUCompanyMaturity] = useState<CompanyMaturityType>()
  const [bannerConfigured, setBannerUnconfigured] = useState<boolean>(true)
  const [loaded, setLoaded] = useState(false)
  const [showTemporaryPasswordAdvisor, setShowTemporaryPasswordAdvisor] =
    useState(false)

  useEffect(() => {
    if (service.dponet.auth.isAuthenticated()) {
      loadData()
    } else {
      navigate(routes.auth.login)
    }

    //eslint-disable-next-line
  }, [])

  const loadData = async () => {
    setLoaded(false)
    const responseMe = await service.dponet.user.me()
    const responseUserCompany = await service.dponet.userCompany.identify()
    await loadScannerStatus()
    await loadCompanyMaturity()
    const user = responseMe?.data?.user
    setUser(user)
    Sentry.setUser(user)
    const userCompany = responseUserCompany?.data?.userCompany
    setUserCompany(userCompany)
    setShowTemporaryPasswordAdvisor(
      user?.temporaryPassword && userCompany?.company?.hasTrayStore
    )
    setLoaded(true)
  }

  const loadOut = () => {
    Sentry.setUser(null)
    setUser({})
    setUserCompany({})
    setUCompanyMaturity(undefined)
    service.dponet.auth.logout()
    navigate(routes.auth.login)
  }

  const loadCompanyMaturity = async () => {
    const responseMaturity = await service.dponet.companyMaturity.get()
    setUCompanyMaturity(responseMaturity?.data?.companyMaturity)
  }

  const mixpanelBrowser = () => {
    mixpanel.init(getMixPanelAuthKey(), {
      debug: false, // TRUE somente em um ambiente de desenvolvimento
      ignore_dnt: true,
      secure_cookie: true,
      cross_site_cookie: false,
      api_transport: 'XHR',
      persistence: 'cookie',
      batch_requests: false,
    })

    if (userCompany?.company) {
      mixpanel.register_once({
        userProfile: userCompany.company.name,
        companyDocument: userCompany.company.document,
      })
    }
  }

  useEffect(() => {
    if (window.document.title !== '') {
      mixpanelBrowser()
    }
    // eslint-disable-next-line
  }, [window.document.title])
  const loadScannerStatus = async () => {
    try {
      const responseBannerConfigured =
        await service.dpoeasy.banner.checkBannerStatus()
      setBannerUnconfigured(responseBannerConfigured?.data?.status)
    } catch {
      setBannerUnconfigured(false)
    }
  }

  const updateCompany = (company: CompanyMinimalType) => {
    setUserCompany((userCompany) => ({
      ...userCompany,
      company: {
        ...userCompany.company,
        ...company,
      },
    }))
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        userCompany,
        companyMaturity,
        bannerConfigured,
        loadOut,
        loaded,
        loadData,
        loadCompanyMaturity,
        setUCompanyMaturity,
        mixpanelBrowser,
        updateCompany,
        showTemporaryPasswordAdvisor,
      }}
    >
      <LoadingTrails open={!loaded} />
      {loaded && <Outlet />}
    </AuthContext.Provider>
  )
}

export default AuthProvider
